import React, { useState } from 'react';
import { consolidations as api } from '../utils/api';
import { fmtBRL, fmtDate, ORDER_STATUS_PT } from '../utils/helpers';
import { parseBRL, parseRate } from '../utils/payments';
import { RESULTS } from './QualityInspections';

// ═══════════════════════════════════════════════════════════════════════════
// Operação consolidada: a consolidação funciona como pedido-pai. O que é comum
// a todos (fase, câmbio da DI, prazos, embarque, parâmetros de importação,
// custos do desembaraço) é lançado uma vez e replicado/rateado nos pedidos —
// cada pedido continua com seu histórico, saldo e custo efetivo corretos.
// ═══════════════════════════════════════════════════════════════════════════
const SHIP = ['Shipped', 'In Customs', 'Delivered'];
const lbl = { fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 3 };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 };
const head = { padding: '10px 16px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 8 };
const title = { fontWeight: 700, fontSize: 13, color: 'var(--pantone)', flex: 1 };

export const COST_CATS = {
  frete_internacional: 'Frete internacional', seguro: 'Seguro', despachante: 'Despachante', armazenagem: 'Armazenagem',
  thc: 'THC / capatazia', siscomex: 'Siscomex', frete_nacional: 'Frete nacional', outros: 'Outros (taxas)',
};
const COST_BASIS = { fob: 'Pelo FOB', cbm: 'Pelo CBM', weight: 'Pelo peso', equal: 'Partes iguais' };

const TABS = {
  status:   '🚦 Fase',
  exchange: '💱 Câmbio',
  delivery: '🏭 Prazos / FOB',
  shipment: '🚢 Embarque',
  import:   '🧾 Parâmetros de importação',
};

// ── Ações em lote ───────────────────────────────────────────────────────────
export function BatchUpdate({ c, run }) {
  const [tab, setTab] = useState('status');
  const [sel, setSel] = useState(() => c.orders.map(o => o.id));
  const [f, setF] = useState({ status: '', rate: c.di_exchange_rate ? String(c.di_exchange_rate).replace('.', ',') : '', rmb_rate: '', fromDi: !!c.di_exchange_rate,
    fob_ready_date: '', fob_ready_actual: '', delivery_transit_days: '', estimated_delivery: '',
    carrier: '', tracking_number: '', incoterm: '', estimated_departure: '', actual_departure: '', estimated_arrival: '', actual_arrival: '',
    icms_pct: '', afrmm_pct: '', import_tax_pct: '', trading_fee_pct: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const ids = sel.filter(id => c.orders.some(o => o.id === id));
  const filled = keys => keys.filter(k => String(f[k] ?? '').trim() !== '').reduce((o, k) => ({ ...o, [k]: f[k] }), {});

  function payload() {
    if (tab === 'status') return f.status ? { status: f.status } : null;
    if (tab === 'exchange') {
      const r = parseRate(f.rate);
      return r > 0 ? { exchange: { rate: r, rmb_rate: parseRate(f.rmb_rate) || undefined, source: f.fromDi ? 'di' : undefined } } : null;
    }
    if (tab === 'delivery') { const d = filled(['fob_ready_date', 'fob_ready_actual', 'delivery_transit_days', 'estimated_delivery']); return Object.keys(d).length ? { delivery: d } : null; }
    if (tab === 'shipment') { const d = filled(['carrier', 'tracking_number', 'incoterm', 'estimated_departure', 'actual_departure', 'estimated_arrival', 'actual_arrival']); return Object.keys(d).length ? { shipment: d } : null; }
    const d = filled(['icms_pct', 'afrmm_pct', 'import_tax_pct', 'trading_fee_pct']);
    return Object.keys(d).length ? { import_params: Object.fromEntries(Object.entries(d).map(([k, v]) => [k, String(v).replace(',', '.')])) } : null;
  }

  async function apply() {
    const p = payload();
    if (!p) return run(() => Promise.reject({ response: { data: { error: 'Preencha ao menos um campo' } } }));
    if (!ids.length) return run(() => Promise.reject({ response: { data: { error: 'Marque ao menos um pedido' } } }));
    if (p.status && SHIP.includes(p.status)) {
      const bad = c.orders.filter(o => ids.includes(o.id) && !SHIP.includes(o.status) && (o.quality === 'rejected' || o.quality === 'pending'));
      if (bad.length && !window.confirm(`Vistoria de qualidade ${bad.map(o => `${o.reference}: ${RESULTS[o.quality]?.label}`).join(', ')}. Avançar para ${ORDER_STATUS_PT[p.status]} mesmo assim?`)) return;
    }
    if (p.exchange && !window.confirm(`Trocar o câmbio de ${ids.length} pedido(s) para R$ ${p.exchange.rate.toFixed(4)}? O custo estimado de cada pedido é recalculado.`)) return;
    setSaving(true);
    await run(() => api.apply(c.id, { ...p, order_ids: ids }), `Atualizado em ${ids.length} pedido(s)`);
    setSaving(false);
  }

  const field = (k, label, props = {}) => (
    <div><label style={lbl}>{label}</label><input value={f[k]} aria-label={label} onChange={e => set(k, e.target.value)} {...props} /></div>
  );

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 20 }} data-testid="batch-update">
      <div style={head}><div style={title}>⚙️ Atualizar os pedidos da consolidação</div>
        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>vale para os pedidos marcados · fica no histórico de cada um</span></div>
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {Object.entries(TABS).map(([k, v]) => <button key={k} type="button" className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab(k)}>{v}</button>)}
        </div>

        {tab === 'status' && (
          <div style={grid}>
            <div><label style={lbl}>Nova fase</label>
              <select value={f.status} aria-label="Nova fase" onChange={e => set('status', e.target.value)}>
                <option value="">— escolha —</option>
                {Object.entries(ORDER_STATUS_PT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select></div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', alignSelf: 'end' }}>Depois do embarque a carga é uma só: Embarcado → Em Alfândega (desembaraço da trading) → Entregue.</div>
          </div>
        )}
        {tab === 'exchange' && (
          <div style={grid}>
            {field('rate', 'Câmbio USD (R$)', { placeholder: '5,6123' })}
            {field('rmb_rate', 'RMB por USD (opcional)', { placeholder: '7,10' })}
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'end' }}>
              <input type="checkbox" checked={f.fromDi} onChange={e => set('fromDi', e.target.checked)} /> É o câmbio da DI (registra na consolidação)
            </label>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', gridColumn: '1 / -1' }}>Troca o câmbio travado de cada pedido: recalcula impostos e custo estimado. Os pagamentos já feitos mantêm o câmbio em que foram fechados.</div>
          </div>
        )}
        {tab === 'delivery' && (
          <div style={grid}>
            {field('fob_ready_date', 'Data FOB acordada', { type: 'date' })}
            {field('fob_ready_actual', 'Produção finalizada em', { type: 'date' })}
            {field('delivery_transit_days', 'Dias de transporte', { type: 'number', min: 0 })}
            {field('estimated_delivery', 'Entrega prevista (fixa)', { type: 'date' })}
          </div>
        )}
        {tab === 'shipment' && (
          <div style={grid}>
            {field('carrier', 'Transportadora / armador')}
            {field('tracking_number', 'BL / AWB / container')}
            {field('incoterm', 'Incoterm', { placeholder: 'FOB' })}
            {field('estimated_departure', 'ETD', { type: 'date' })}
            {field('actual_departure', 'Embarcou em', { type: 'date' })}
            {field('estimated_arrival', 'ETA', { type: 'date' })}
            {field('actual_arrival', 'Chegou em', { type: 'date' })}
          </div>
        )}
        {tab === 'import' && (
          <div style={grid}>
            {field('icms_pct', 'ICMS (%)', { placeholder: 'Ex.: 4' })}
            {field('import_tax_pct', 'II (%)')}
            {field('afrmm_pct', 'AFRMM (%)')}
            {field('trading_fee_pct', 'Fee da trading (%)')}
            <div style={{ fontSize: 11, color: 'var(--gray-500)', gridColumn: '1 / -1' }}>Só os campos preenchidos mudam. Na importação via trading o ICMS e o fee costumam ser os mesmos para todos os pedidos da DI.</div>
          </div>
        )}

        <div style={{ marginTop: 14, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--gray-500)', marginRight: 4 }}>Aplicar em:</span>
          {c.orders.map(o => (
            <label key={o.id} style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--gray-200)', borderRadius: 999, padding: '2px 10px' }}>
              <input type="checkbox" checked={sel.includes(o.id)} aria-label={`Aplicar em ${o.reference}`}
                onChange={() => setSel(s => (s.includes(o.id) ? s.filter(x => x !== o.id) : [...s, o.id]))} />
              <span className="mono">{o.reference}</span><span style={{ color: 'var(--gray-400)' }}>{ORDER_STATUS_PT[o.status] || o.status}</span>
            </label>
          ))}
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-primary btn-sm" disabled={saving || !ids.length} onClick={apply}>
            {saving ? '⏳' : `Aplicar em ${ids.length} pedido${ids.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Custos compartilhados (rateados) ────────────────────────────────────────
export function SharedCosts({ c, run }) {
  const [form, setForm] = useState(null);
  const set = (k, v) => setForm(x => ({ ...x, [k]: v }));
  async function save() {
    const amount = parseBRL(form.amount);
    const ok = await run(() => api.addCost(c.id, { category: form.category, description: form.description, amount, currency: form.currency,
      exchange_rate: form.currency === 'BRL' ? undefined : parseRate(form.rate), allocation_basis: form.basis, paid_by: form.paid_by }), 'Custo lançado e rateado');
    if (ok) setForm(null);
  }
  const items = c.cost_items || [];
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 20 }} data-testid="shared-costs">
      <div style={head}><div style={title}>🚚 Custos consolidados (frete, despachante, armazenagem…)</div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setForm({ category: 'frete_internacional', description: '', amount: '', currency: 'BRL', rate: '', basis: 'fob', paid_by: 'charth' })}>+ Lançar custo</button></div>
      {form && (
        <div style={{ padding: 16, background: 'var(--accent-light, #FAF5F6)', borderBottom: '1px solid var(--gray-100)' }} data-testid="shared-cost-form">
          <div style={grid}>
            <div><label style={lbl}>Tipo</label><select value={form.category} aria-label="Tipo de custo" onChange={e => set('category', e.target.value)}>
              {Object.entries(COST_CATS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            <div><label style={lbl}>Descrição</label><input value={form.description} aria-label="Descrição do custo" placeholder="Ex.: frete marítimo 40HC" onChange={e => set('description', e.target.value)} /></div>
            <div><label style={lbl}>Valor</label><input value={form.amount} aria-label="Valor do custo" placeholder="0,00" onChange={e => set('amount', e.target.value)} /></div>
            <div><label style={lbl}>Moeda</label><select value={form.currency} aria-label="Moeda do custo" onChange={e => set('currency', e.target.value)}>
              <option value="BRL">R$</option><option value="USD">US$</option><option value="RMB">¥</option></select></div>
            {form.currency !== 'BRL' && <div><label style={lbl}>Câmbio (R$)</label><input value={form.rate} aria-label="Câmbio do custo" placeholder="5,60" onChange={e => set('rate', e.target.value)} /></div>}
            <div><label style={lbl}>Rateio</label><select value={form.basis} aria-label="Rateio do custo" onChange={e => set('basis', e.target.value)}>
              {Object.entries(COST_BASIS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            <div><label style={lbl}>Quem paga</label><select value={form.paid_by} aria-label="Quem paga" onChange={e => set('paid_by', e.target.value)}>
              <option value="charth">Charth (via trading)</option><option value="fornecedor">Fornecedor (embutido)</option><option value="forwarder">Forwarder</option><option value="agente">Agente</option></select></div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 8 }}>Impostos da DI não entram aqui: eles saem do NCM de cada pedido. O numerário de impostos pago à trading vai em “Pagamentos à trading”.</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setForm(null)}>Cancelar</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={save}>💾 Lançar e ratear</button>
          </div>
        </div>
      )}
      <table className="table">
        <thead><tr><th>Custo</th><th style={{ textAlign: 'right' }}>Valor</th><th style={{ textAlign: 'right' }}>R$</th><th>Rateio</th><th style={{ width: 40 }}></th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 16 }}>Nenhum custo consolidado.</td></tr>}
          {items.map(ci => (
            <tr key={ci.id} data-testid="shared-cost-row">
              <td>{COST_CATS[ci.category] || ci.category}{ci.description && <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{ci.description}</div>}
                {ci.paid_by !== 'charth' && <div style={{ fontSize: 10, color: 'var(--gray-400)' }}>pago por {ci.paid_by} · não entra no saldo</div>}</td>
              <td className="mono" style={{ textAlign: 'right' }}>{ci.currency === 'BRL' ? fmtBRL(ci.amount) : `${ci.currency} ${Number(ci.amount).toFixed(2)}`}</td>
              <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{fmtBRL(ci.amount_brl)}</td>
              <td style={{ fontSize: 11 }}><div style={{ color: 'var(--gray-500)' }}>{COST_BASIS[ci.allocation_basis] || ci.allocation_basis}</div>
                {ci.allocations.map(a => <div key={a.quotation_id}><span className="mono">{a.reference}</span> {fmtBRL(a.amount_brl)}</div>)}</td>
              <td><button type="button" className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--error)' }} title="Excluir custo"
                onClick={() => window.confirm('Excluir este custo e o rateio dele nos pedidos?') && run(() => api.deleteCost(c.id, ci.id), 'Custo excluído')}>🗑️</button></td>
            </tr>
          ))}
          {items.length > 0 && <tr style={{ fontWeight: 700 }}><td colSpan={2}>Total</td><td className="mono" style={{ textAlign: 'right' }}>{fmtBRL(c.totals.shared_costs_brl)}</td><td colSpan={2}></td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ── Desembaraço em nome da trading + NF de refaturamento ────────────────────
const toInput = v => (v == null ? '' : String(v).replace('.', ','));
export function Clearance({ c, run }) {
  const init = () => ({ di_number: c.di_number || '', di_date: c.di_date || '', di_exchange_rate: toInput(c.di_exchange_rate),
    nf_number: c.nf_number || '', nf_date: c.nf_date || '', nf_amount_brl: toInput(c.nf_amount_brl),
    nf_icms_brl: toInput(c.nf_icms_brl), nf_ipi_brl: toInput(c.nf_ipi_brl), nf_pis_cofins_brl: toInput(c.nf_pis_cofins_brl) });
  const [edit, setEdit] = useState(null);
  const set = (k, v) => setEdit(x => ({ ...x, [k]: v }));
  const rf = c.refaturamento;
  async function save() {
    const money = k => (String(edit[k]).trim() === '' ? '' : String(parseBRL(edit[k])));
    const ok = await run(() => api.update(c.id, { ...edit, di_exchange_rate: edit.di_exchange_rate === '' ? '' : String(parseRate(edit.di_exchange_rate)),
      nf_amount_brl: money('nf_amount_brl'), nf_icms_brl: money('nf_icms_brl'), nf_ipi_brl: money('nf_ipi_brl'), nf_pis_cofins_brl: money('nf_pis_cofins_brl') }), 'Desembaraço/refaturamento salvo');
    if (ok) setEdit(null);
  }
  const f = (k, label, props = {}) => <div><label style={lbl}>{label}</label><input value={edit[k]} aria-label={label} onChange={e => set(k, e.target.value)} {...props} /></div>;
  const diff = v => <span style={{ color: Math.abs(v) < 1 ? '#276749' : v > 0 ? '#C53030' : '#276749', fontWeight: 700 }}>{v > 0 ? '+' : ''}{fmtBRL(v)}</span>;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 20 }} data-testid="clearance">
      <div style={head}><div style={title}>🛃 Desembaraço pela trading e refaturamento nacional</div>
        {!edit && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEdit(init())}>✏️ Editar</button>}</div>
      {edit ? (
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 6 }}>DI / DUIMP (em nome da trading)</div>
          <div style={grid}>{f('di_number', 'Nº da DI')}{f('di_date', 'Data do registro/desembaraço', { type: 'date' })}{f('di_exchange_rate', 'Câmbio da DI (R$)', { placeholder: '5,6123' })}</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', margin: '14px 0 6px' }}>NF de venda da trading para a Charth</div>
          <div style={grid}>{f('nf_number', 'Nº da NF')}{f('nf_date', 'Emissão da NF', { type: 'date' })}{f('nf_amount_brl', 'Valor total da NF (R$)')}
            {f('nf_icms_brl', 'ICMS destacado (R$)')}{f('nf_ipi_brl', 'IPI destacado (R$)')}{f('nf_pis_cofins_brl', 'PIS + COFINS (crédito, R$)')}</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEdit(null)}>Cancelar</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={save}>💾 Salvar</button>
          </div>
        </div>
      ) : (
        <div style={{ padding: 16, fontSize: 13 }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: rf ? 12 : 0 }}>
            <div><span style={lbl}>DI</span>{c.di_number || <span style={{ color: 'var(--gray-400)' }}>não registrada</span>}{c.di_date ? ` · ${fmtDate(c.di_date)}` : ''}</div>
            <div><span style={lbl}>Câmbio da DI</span>{c.di_exchange_rate ? `R$ ${Number(c.di_exchange_rate).toFixed(4)}` : '—'}</div>
            <div><span style={lbl}>NF de refaturamento</span>{c.nf_number || <span style={{ color: 'var(--gray-400)' }}>ainda não emitida</span>}{c.nf_date ? ` · ${fmtDate(c.nf_date)}` : ''}</div>
            <div><span style={lbl}>Pago à trading (tudo)</span><span className="mono">{fmtBRL(c.totals.paid_to_trading_brl)}</span></div>
          </div>
          {rf && (
            <>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: 10, background: 'var(--gray-50)', borderRadius: 'var(--radius)' }} data-testid="refat-summary">
                <div><span style={lbl}>Valor da NF</span><span className="mono">{fmtBRL(rf.nf_amount_brl)}</span></div>
                <div><span style={lbl}>Créditos (ICMS/IPI/PIS/COFINS)</span><span className="mono">− {fmtBRL(rf.credits_brl)}</span></div>
                <div><span style={lbl}>Custo líquido real</span><span className="mono" style={{ fontWeight: 700 }}>{fmtBRL(rf.net_cost_brl)}</span></div>
                <div><span style={lbl}>Custo estimado no sistema</span><span className="mono">{fmtBRL(rf.estimated_cost_brl)}</span></div>
                <div><span style={lbl}>Diferença</span>{diff(rf.diff_vs_estimated_brl)}</div>
                <div><span style={lbl}>NF − pago à trading</span>{diff(rf.to_pay_brl)}<div style={{ fontSize: 10, color: 'var(--gray-400)' }}>{rf.to_pay_brl > 1 ? 'ainda a acertar com a trading' : rf.to_pay_brl < -1 ? 'pago além da NF' : 'fechado'}</div></div>
              </div>
              <table className="table" style={{ marginTop: 10 }}>
                <thead><tr><th>Pedido</th><th style={{ textAlign: 'right' }}>Custo real (NF, rateio FOB)</th><th style={{ textAlign: 'right' }}>Estimado</th><th style={{ textAlign: 'right' }}>Diferença</th></tr></thead>
                <tbody>{rf.per_order.map(o => (
                  <tr key={o.quotation_id}><td className="mono">{o.reference}</td><td className="mono" style={{ textAlign: 'right' }}>{fmtBRL(o.net_cost_brl)}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{fmtBRL(o.estimated_cost_brl)}</td><td className="mono" style={{ textAlign: 'right' }}>{diff(o.diff_brl)}</td></tr>
                ))}</tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
